import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  BarChart3,
  Bell,
  Bot,
  BookOpen,
  BookText,
  Building2,
  Calculator,
  CalendarCheck,
  CheckCircle,
  FileInput,
  FileSpreadsheet,
  FileText,
  Flame,
  FolderKanban,
  GraduationCap,
  Handshake,
  KanbanSquare,
  LayoutDashboard,
  ListChecks,
  Mail,
  Megaphone,
  Monitor,
  Network,
  Receipt,
  Scale,
  Settings,
  Shield,
  Sparkles,
  Tag,
  Trash2,
  Users,
  Zap,
} from 'lucide-react';

/**
 * Roles por defecto de la instalación comercial del CRM/ERP.
 * Deben coincidir con los `name` de los roles en la base de datos (Spatie).
 * Cada cliente puede crear roles adicionales desde Configuración → Roles y permisos;
 * para que un rol nuevo vea menús, añádelo a los arrays `roles` de abajo.
 */
export type UserRole =
  | 'super-admin'
  | 'super_admin'
  | 'admin'
  | 'admin_rrhh'
  | 'coordinador'
  | 'gerente'
  | 'comercial'
  | 'marketing'
  | 'finanzas'
  | 'administracion'
  | 'rrhh'
  | 'soporte'
  | 'tecnico'
  | 'directivo'
  | 'empleado';

export type SidebarItem = {
  label: string;
  to: string;
  icon: LucideIcon;
  enabled: boolean;
  badge?: number;
  /**
   * Roles que ven este elemento (fallback). Se usa solo si `anyPermission`
   * no está definido — p. ej. en áreas de sistema sin un permiso propio.
   */
  roles: UserRole[];
  /**
   * Permisos que dan acceso a este elemento: se muestra si el usuario tiene
   * AL MENOS UNO. Tiene prioridad sobre `roles`. Así, al editar los permisos
   * de un rol (o crear uno nuevo) en Configuración → Roles y permisos, el menú
   * se adapta solo, sin tocar código.
   */
  anyPermission?: string[];
  children?: SidebarItem[];
  /** Si es true, al pulsar el grupo también navega a su ruta principal. */
  navigateOnClick?: boolean;
};

// ── Grupos de roles reutilizables (legibilidad) ────────────────────────────
const ADMINS: UserRole[] = ['super-admin', 'admin'];
const FINANZAS: UserRole[] = ['super-admin', 'admin', 'gerente', 'finanzas', 'administracion'];
const VENTAS: UserRole[] = ['super-admin', 'admin', 'gerente', 'comercial', 'marketing'];
const TODOS: UserRole[] = [
  'super-admin',
  'admin',
  'gerente',
  'comercial',
  'marketing',
  'finanzas',
  'administracion',
  'rrhh',
  'soporte',
  'empleado',
];

export const sidebarItems: SidebarItem[] = [
  {
    label: 'Panel de administración',
    to: '/admin-panel',
    icon: LayoutDashboard,
    enabled: true,
    roles: ['super-admin', 'admin', 'gerente'],
  },
  {
    label: 'Asistente IA',
    to: '/ai',
    icon: Bot,
    enabled: true,
    roles: ['super-admin', 'admin', 'gerente', 'rrhh', 'finanzas', 'administracion'],
    anyPermission: ['manage-roles', 'view-employees', 'view-accounting'],
  },
  {
    label: 'Automatizaciones',
    to: '/automations',
    icon: Zap,
    enabled: true,
    roles: ['super-admin', 'admin', 'gerente', 'finanzas', 'directivo'],
    anyPermission: ['view-invoices', 'manage-payroll', 'manage-roles'],
  },
  {
    label: 'Recursos Humanos',
    to: '/rrhh',
    icon: Users,
    enabled: true,
    roles: ['super-admin', 'admin', 'gerente', 'rrhh', 'empleado'],
    anyPermission: ['view-employees'],
  },
  {
    label: 'Facturación',
    to: '/billing',
    icon: FileText,
    enabled: true,
    roles: ['super-admin', 'admin', 'gerente', 'finanzas', 'administracion', 'comercial'],
    anyPermission: ['view-invoices'],
    children: [
      {
        label: 'Dashboard',
        to: '/billing',
        icon: BarChart3,
        enabled: true,
        roles: ['super-admin', 'admin', 'gerente', 'finanzas', 'administracion', 'comercial'],
        anyPermission: ['view-invoices'],
      },
      {
        label: 'Facturas',
        to: '/billing/invoices',
        icon: Receipt,
        enabled: true,
        roles: ['super-admin', 'admin', 'gerente', 'finanzas', 'administracion', 'comercial'],
        anyPermission: ['view-invoices'],
      },
      {
        label: 'Presupuestos',
        to: '/billing/quotes',
        icon: FileInput,
        enabled: true,
        roles: ['super-admin', 'admin', 'gerente', 'finanzas', 'administracion', 'comercial'],
        anyPermission: ['view-invoices'],
      },
      {
        label: 'Clientes',
        to: '/billing/clients',
        icon: Users,
        enabled: true,
        roles: ['super-admin', 'admin', 'gerente', 'finanzas', 'administracion', 'comercial'],
        anyPermission: ['manage-clients', 'view-invoices'],
      },
      {
        label: 'Conceptos',
        to: '/billing/concepts',
        icon: Tag,
        enabled: true,
        roles: FINANZAS,
        anyPermission: ['view-invoices'],
      },
      {
        label: 'Emisores',
        to: '/billing/issuers',
        icon: Building2,
        enabled: true,
        roles: ['super-admin', 'admin', 'gerente', 'finanzas'],
        anyPermission: ['manage-issuers'],
      },
    ],
  },
  {
    label: 'Compras',
    to: '/purchases',
    icon: Receipt,
    enabled: true,
    roles: FINANZAS,
    anyPermission: ['view-purchases'],
  },
  {
    label: 'Contabilidad',
    to: '/accounting/accounts',
    icon: Calculator,
    enabled: true,
    roles: ['super-admin', 'admin', 'gerente', 'finanzas'],
    anyPermission: ['view-accounting'],
    children: [
      {
        label: 'Plan de cuentas',
        to: '/accounting/accounts',
        icon: ListChecks,
        enabled: true,
        roles: ['super-admin', 'admin', 'gerente', 'finanzas'],
        anyPermission: ['view-accounting'],
      },
      {
        label: 'Libro diario',
        to: '/accounting/journal',
        icon: BookText,
        enabled: true,
        roles: ['super-admin', 'admin', 'gerente', 'finanzas'],
        anyPermission: ['view-accounting'],
      },
      {
        label: 'Mayor',
        to: '/accounting/ledger',
        icon: BookOpen,
        enabled: true,
        roles: ['super-admin', 'admin', 'gerente', 'finanzas'],
        anyPermission: ['view-mayor', 'view-accounting'],
      },
      {
        label: 'Informes',
        to: '/accounting/reports',
        icon: Scale,
        enabled: true,
        roles: ['super-admin', 'admin', 'gerente', 'finanzas'],
        anyPermission: ['view-balance', 'view-pyg', 'view-accounting'],
      },
      {
        label: 'Modelo 303',
        to: '/accounting/vat',
        icon: FileSpreadsheet,
        enabled: true,
        roles: ['super-admin', 'admin', 'gerente', 'finanzas'],
        anyPermission: ['generate-modelo-303', 'view-accounting'],
      },
      {
        label: 'Configuración',
        to: '/accounting/config',
        icon: Settings,
        enabled: true,
        roles: ['super-admin', 'admin', 'finanzas'],
        anyPermission: ['manage-chart-of-accounts'],
      },
    ],
  },
  {
    label: 'Tareas',
    to: '/tasks',
    icon: ListChecks,
    enabled: true,
    roles: ['super-admin', 'super_admin', 'admin', 'admin_rrhh', 'coordinador', 'tecnico', 'empleado'],
  },
  {
    label: 'Proyectos',
    to: '/projects',
    icon: FolderKanban,
    enabled: true,
    roles: ['super-admin', 'admin', 'directivo', 'soporte'],
    anyPermission: ['view-projects'],
  },
  {
    label: 'Comercial',
    to: '/commercial',
    icon: Handshake,
    enabled: true,
    roles: ['super-admin', 'admin', 'gerente', 'comercial', 'finanzas'],
    anyPermission: ['view-commercial'],
    children: [
      {
        label: 'Resumen',
        to: '/commercial',
        icon: BarChart3,
        enabled: true,
        roles: ['super-admin', 'admin', 'gerente', 'comercial', 'finanzas'],
        anyPermission: ['view-commercial'],
      },
      {
        label: 'Valoraciones',
        to: '/commercial/valuations',
        icon: FileSpreadsheet,
        enabled: true,
        roles: ['super-admin', 'admin', 'gerente', 'comercial'],
        anyPermission: ['view-commercial'],
      },
      {
        label: 'Costes',
        to: '/commercial/costs',
        icon: Calculator,
        enabled: true,
        roles: ['super-admin', 'admin', 'gerente', 'finanzas'],
        anyPermission: ['manage-commercial-costs'],
      },
      {
        label: 'Beneficios',
        to: '/commercial/benefits',
        icon: Activity,
        enabled: true,
        roles: ['super-admin', 'admin', 'gerente', 'comercial', 'finanzas'],
        anyPermission: ['view-commercial-benefits'],
      },
      {
        label: 'Configuración',
        to: '/commercial/settings',
        icon: Settings,
        enabled: true,
        roles: ['super-admin', 'admin', 'gerente', 'finanzas'],
        anyPermission: ['manage-commercial-settings'],
      },
    ],
  },
  {
    label: 'Marketing',
    to: '/marketing',
    icon: Megaphone,
    enabled: true,
    roles: VENTAS,
    anyPermission: ['view-marketing'],
    children: [
      {
        label: 'Dashboard',
        to: '/marketing',
        icon: BarChart3,
        enabled: true,
        roles: VENTAS,
        anyPermission: ['view-marketing'],
      },
      {
        label: 'Analytics',
        to: '/marketing/analytics',
        icon: Activity,
        enabled: true,
        roles: VENTAS,
        anyPermission: ['view-marketing'],
      },
      {
        label: 'Contactos',
        to: '/marketing/contacts',
        icon: Users,
        enabled: true,
        roles: VENTAS,
        anyPermission: ['view-marketing'],
      },
      {
        label: 'Pipeline',
        to: '/marketing/pipeline',
        icon: KanbanSquare,
        enabled: true,
        roles: VENTAS,
        anyPermission: ['view-marketing'],
      },
      {
        label: 'Agenda',
        to: '/marketing/agenda',
        icon: CalendarCheck,
        enabled: true,
        roles: VENTAS,
        anyPermission: ['view-marketing'],
      },
      {
        label: 'Campañas',
        to: '/marketing/campaigns',
        icon: Mail,
        enabled: true,
        roles: VENTAS,
        anyPermission: ['view-marketing'],
      },
      {
        label: 'Automatizaciones',
        to: '/marketing/automations',
        icon: Zap,
        enabled: true,
        roles: VENTAS,
        anyPermission: ['view-marketing'],
      },
      {
        label: 'Scoring & Alertas',
        to: '/marketing/scoring',
        icon: Flame,
        enabled: true,
        roles: VENTAS,
        anyPermission: ['view-marketing'],
      },
      {
        label: 'Formularios',
        to: '/marketing/forms',
        icon: FileInput,
        enabled: true,
        roles: VENTAS,
        anyPermission: ['view-marketing'],
      },
      {
        label: 'Contenidos',
        to: '/marketing/content',
        icon: ListChecks,
        enabled: true,
        roles: VENTAS,
        anyPermission: ['view-marketing'],
      },
    ],
  },
  {
    label: 'IT',
    to: '/it',
    icon: Monitor,
    enabled: true,
    roles: ['super-admin', 'admin', 'gerente', 'soporte', 'empleado'],
    anyPermission: ['view-tickets', 'create-tickets'],
  },
  {
    label: 'Campus',
    to: '/campus',
    icon: GraduationCap,
    enabled: false,
    roles: TODOS,
  },
  {
    label: 'Certificaciones',
    to: '/certificaciones',
    icon: CheckCircle,
    enabled: true,
    roles: TODOS,
    anyPermission: ['view-quality', 'manage-quality'],
    navigateOnClick: true,
    children: [
      {
        label: 'CALIDAD y MEDIOAMBIENTE (ISO 9001-14001)',
        to: '/certificaciones/iso-9001-14001',
        icon: CheckCircle,
        enabled: true,
        roles: TODOS,
        anyPermission: ['view-quality', 'manage-quality'],
      },
      {
        label: 'PRIVACIDAD y SEGURIDAD (ISO 27001-27701)',
        to: '/certificaciones/iso-27001-27701',
        icon: Shield,
        enabled: true,
        roles: TODOS,
        anyPermission: ['view-quality', 'manage-quality'],
      },
    ],
  },
  {
    label: 'Operativa',
    to: '/operative',
    icon: Settings,
    enabled: false,
    roles: ['super-admin', 'admin', 'gerente'],
  },
  {
    label: 'Notificaciones',
    to: '/notifications',
    icon: Bell,
    enabled: true,
    roles: TODOS,
  },
  {
    label: 'Configuración',
    to: '/settings',
    icon: Settings,
    enabled: true,
    roles: ADMINS,
    anyPermission: ['manage-roles', 'edit-employees'],
    children: [
      {
        label: 'Panel',
        to: '/settings',
        icon: LayoutDashboard,
        enabled: true,
        roles: ADMINS,
        anyPermission: ['manage-roles'],
      },
      {
        label: 'Recursos Humanos',
        to: '/settings/rrhh',
        icon: Users,
        enabled: true,
        roles: ADMINS,
        anyPermission: ['manage-roles', 'edit-employees'],
      },
      {
        label: 'Facturación',
        to: '/settings/billing',
        icon: FileText,
        enabled: true,
        roles: ADMINS,
        anyPermission: ['manage-roles', 'manage-issuers'],
      },
      {
        label: 'Empresas y departamentos',
        to: '/settings/organization',
        icon: Network,
        enabled: true,
        roles: ADMINS,
        anyPermission: ['manage-roles', 'edit-employees', 'manage-issuers'],
      },
      {
        label: 'Equipo',
        to: '/settings/team',
        icon: Users,
        enabled: true,
        roles: ADMINS,
        anyPermission: ['manage-roles'],
      },
      {
        label: 'Roles y permisos',
        to: '/settings/roles',
        icon: Shield,
        enabled: true,
        roles: ADMINS,
        anyPermission: ['manage-roles'],
      },
      {
        label: 'Auditoría',
        to: '/settings/audit',
        icon: Activity,
        enabled: true,
        roles: ADMINS,
        anyPermission: ['manage-roles'],
      },
      {
        label: 'Marca',
        to: '/settings/branding',
        icon: Sparkles,
        enabled: true,
        roles: ADMINS,
        anyPermission: ['manage-roles'],
      },
      {
        label: 'Limpieza de datos',
        to: '/settings/data-cleanup',
        icon: Trash2,
        enabled: true,
        roles: ['super-admin', 'admin', 'rrhh', 'admin_rrhh'],
      },
    ],
  },
];
